import { createGlobalStyle, css, CSSProperties } from 'styled-components';
import { fontList, fontsForWeb, getFontStyle } from '../fonts';
import { DesignSettings } from '../interfaces';
import { merge } from 'lodash';

export interface BackgroundOption {
    useGradient?: boolean;
    useImage?: boolean;
}

export const generateBackgroundObject = (designSettings: DesignSettings, options?: BackgroundOption) => {
    const { useImage, useGradient } = merge(
        {
            useGradient: true,
            useImage: true,
        },
        options,
    );
    let color = designSettings.colors.background.color;
    let image = useImage ? designSettings.colors.background.backgroundImage : null;
    const backgroundStyle = designSettings.colors.background.backgroundStyle;

    if (useGradient && !image && designSettings.colors.background.color?.includes('linear-gradient')) {
        image = designSettings.colors.background.color;
        color = undefined;
    }
    const res: CSSProperties = {
        backgroundColor: color,
    };
    if (image) {
        if (image.includes('linear-gradient')) {
            res.backgroundImage = image;
        } else {
            res.backgroundImage = `url(${image})`;
            if (backgroundStyle === 'fill') {
                res.backgroundSize = 'cover';
                res.backgroundPosition = 'center center';
            } else {
                res.backgroundRepeat = 'repeat';
            }
        }
    }
    return res;
};

export const fontFaceCSS = css(
    ({ theme }) => `
    ${Object.keys(fontList)
        .map((f) => fontsForWeb(theme.fontEndpoint, f))
        .join('')}
`,
);
const fontCSS = css(({ theme }) => ({
    body: {
        ...getFontStyle(theme.designSettings, 'secondary', 'regular'),
        color: theme.designSettings.colors.text.color,
    },
}));

const animationCSS = css`
    .fade-enter {
        opacity: 0;
        transition: opacity 0.5s ease;
    }

    .fade-enter-active {
        opacity: 1;
        transition: opacity 0.5s ease;
    }

    .fade-exit {
        opacity: 1;
        transition: opacity 0.5s ease;
    }

    .fade-exit-active {
        opacity: 0;
        transition: opacity 0.5s ease;
    }
`;
const resetCSS = css`
    html,
    body {
        overflow-wrap: break-word;
        -webkit-hyphens: auto;
        hyphens: auto;
        word-break: break-word;
        margin: 0;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
    }

    blockquote,
    dl,
    dd,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    hr,
    figure,
    p,
    pre {
        margin: 0;
    }

    button {
        background-color: transparent;
        background-image: none;
    }

    fieldset {
        margin: 0;
        padding: 0;
    }

    ol,
    ul {
        list-style: none;
        margin: 0;
        padding: 0;
    }

    body {
        font-family: inherit;
        line-height: inherit;
    }

    *,
    ::before,
    ::after {
        box-sizing: border-box; /* 1 */
        border-width: 0; /* 2 */
        border-style: solid; /* 2 */
        border-color: currentColor; /* 2 */
        outline: none;
    }

    hr {
        border-top-width: 1px;
    }

    img {
        border-style: solid;
    }

    textarea {
        resize: vertical;
    }

    input::placeholder,
    textarea::placeholder {
        opacity: 1;
        color: #a1a1aa;
    }

    button,
    [role='button'] {
        cursor: pointer;
    }

    table {
        border-collapse: collapse;
    }

    a {
        color: inherit;
        text-decoration: inherit;
    }

    button,
    input,
    optgroup,
    select,
    textarea {
        padding: 0;
        line-height: inherit;
        color: inherit;
    }

    pre,
    code,
    kbd,
    samp {
        font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
    }

    img,
    svg,
    video,
    canvas,
    audio,
    iframe,
    embed,
    object {
        display: block;
        vertical-align: middle;
    }

    img,
    video {
        max-width: 100%;
        height: auto;
    }
`;

const swiperSlideCss = css`
    @font-face {
        font-family: swiper-icons;
        src: url('data:application/font-woff;charset=utf-8;base64, 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');
        font-weight: 400;
        font-style: normal;
    }
    .swiper {
        margin-left: auto;
        margin-right: auto;
        position: relative;
        overflow: hidden;
        list-style: none;
        padding: 0;
        z-index: 1;
    }
    .swiper-vertical > .swiper-wrapper {
        flex-direction: column;
    }
    .swiper-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: flex;
        transition-property: transform;
        box-sizing: content-box;
        align-items: stretch;
    }
    .swiper-android .swiper-slide,
    .swiper-wrapper {
        transform: translate3d(0px, 0, 0);
    }
    .swiper-pointer-events {
        touch-action: pan-y;
    }
    .swiper-pointer-events.swiper-vertical {
        touch-action: pan-x;
    }
    .swiper-slide {
        flex-shrink: 0;
        width: auto;
        position: relative;
        transition-property: transform;
        display: flex;
        flex-direction: column;
    }
    .swiper-slide-invisible-blank {
        visibility: hidden;
    }
    .swiper-autoheight,
    .swiper-autoheight .swiper-slide {
        height: auto;
    }
    .swiper-autoheight .swiper-wrapper {
        align-items: flex-start;
        transition-property: transform, height;
    }
    .swiper-backface-hidden .swiper-slide {
        transform: translateZ(0);
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
    }
    .swiper-3d,
    .swiper-3d.swiper-css-mode .swiper-wrapper {
        perspective: 1200px;
    }
    .swiper-3d .swiper-cube-shadow,
    .swiper-3d .swiper-slide,
    .swiper-3d .swiper-slide-shadow,
    .swiper-3d .swiper-slide-shadow-bottom,
    .swiper-3d .swiper-slide-shadow-left,
    .swiper-3d .swiper-slide-shadow-right,
    .swiper-3d .swiper-slide-shadow-top,
    .swiper-3d .swiper-wrapper {
        transform-style: preserve-3d;
    }
    .swiper-3d .swiper-slide-shadow,
    .swiper-3d .swiper-slide-shadow-bottom,
    .swiper-3d .swiper-slide-shadow-left,
    .swiper-3d .swiper-slide-shadow-right,
    .swiper-3d .swiper-slide-shadow-top {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 10;
    }
    .swiper-3d .swiper-slide-shadow {
        background: rgba(0, 0, 0, 0.15);
    }
    .swiper-3d .swiper-slide-shadow-left {
        background-image: linear-gradient(to left, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
    }
    .swiper-3d .swiper-slide-shadow-right {
        background-image: linear-gradient(to right, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
    }
    .swiper-3d .swiper-slide-shadow-top {
        background-image: linear-gradient(to top, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
    }
    .swiper-3d .swiper-slide-shadow-bottom {
        background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
    }
    .swiper-css-mode > .swiper-wrapper {
        overflow: auto;
        scrollbar-width: none;
        -ms-overflow-style: none;
    }
    .swiper-css-mode > .swiper-wrapper::-webkit-scrollbar {
        display: none;
    }
    .swiper-css-mode > .swiper-wrapper > .swiper-slide {
        scroll-snap-align: start start;
    }
    .swiper-horizontal.swiper-css-mode > .swiper-wrapper {
        scroll-snap-type: x mandatory;
    }
    .swiper-vertical.swiper-css-mode > .swiper-wrapper {
        scroll-snap-type: y mandatory;
    }
    .swiper-centered > .swiper-wrapper::before {
        content: '';
        flex-shrink: 0;
        order: 9999;
    }
    .swiper-centered.swiper-horizontal > .swiper-wrapper > .swiper-slide:first-child {
        margin-inline-start: var(--swiper-centered-offset-before);
    }
    .swiper-centered.swiper-horizontal > .swiper-wrapper::before {
        height: 100%;
        min-height: 1px;
        width: var(--swiper-centered-offset-after);
    }
    .swiper-centered.swiper-vertical > .swiper-wrapper > .swiper-slide:first-child {
        margin-block-start: var(--swiper-centered-offset-before);
    }
    .swiper-centered.swiper-vertical > .swiper-wrapper::before {
        width: 100%;
        min-width: 1px;
        height: var(--swiper-centered-offset-after);
    }
    .swiper-centered > .swiper-wrapper > .swiper-slide {
        scroll-snap-align: center center;
    }
    .swiper-button-next,
    .swiper-button-prev {
        border-radius: 50%;
        width: 20px;
        height: 20px;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
        box-shadow: 0.75px 2.25px 5.25px rgba(0, 0, 0, 0.15);
    }
    .swiper-button-prev {
        left: 12px;
        filter: drop-shadow(1px 1px 2px rgba(0, 0, 05, 0.3));
        background: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 19 19' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M9.5 19C14.7467 19 19 14.7467 19 9.5C19 4.25329 14.7467 0 9.5 0C4.25329 0 0 4.25329 0 9.5C0 14.7467 4.25329 19 9.5 19ZM11.2746 7.27464C11.5662 6.98305 11.5662 6.51029 11.2746 6.21869C10.983 5.9271 10.5103 5.9271 10.2187 6.21869L7.21869 9.21869C6.9271 9.51028 6.9271 9.98305 7.21869 10.2746L10.2187 13.2746C10.5103 13.5662 10.983 13.5662 11.2746 13.2746C11.5662 12.983 11.5662 12.5103 11.2746 12.2187L8.80261 9.74667L11.2746 7.27464Z' fill='white'/%3E%3C/svg%3E");
    }
    .swiper-button-next {
        right: 12px;
        filter: drop-shadow(1px 1px 2px rgba(0, 0, 05, 0.3));
        background: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 19 19' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M9.5 19C4.25329 19 0 14.7467 0 9.5C0 4.25329 4.25329 0 9.5 0C14.7467 0 19 4.25329 19 9.5C19 14.7467 14.7467 19 9.5 19ZM7.72536 7.27464C7.43377 6.98305 7.43377 6.51029 7.72536 6.21869C8.01695 5.9271 8.48971 5.9271 8.78131 6.21869L11.7813 9.21869C12.0729 9.51028 12.0729 9.98305 11.7813 10.2746L8.78131 13.2746C8.48972 13.5662 8.01695 13.5662 7.72536 13.2746C7.43377 12.983 7.43377 12.5103 7.72536 12.2187L10.1974 9.74667L7.72536 7.27464Z' fill='white'/%3E%3C/svg%3E");
    }
    .swiper-button-disabled {
        display: none;
    }
    .swiper-pagination {
        text-align: center;
        transition: 0.3s opacity;
        transform: translate3d(0, 0, 0);
        z-index: 10;
        padding-top: 10px;
    }
    .swiper-pagination.swiper-pagination-hidden {
        opacity: 0;
    }
    .swiper-pagination-disabled > .swiper-pagination,
    .swiper-pagination.swiper-pagination-disabled {
        display: none !important;
    }
    .swiper-horizontal > .swiper-pagination-bullets,
    .swiper-pagination-bullets.swiper-pagination-horizontal,
    .swiper-pagination-custom,
    .swiper-pagination-fraction {
        left: 0;
        width: 100%;
    }
    .swiper-pagination-bullets-dynamic {
        overflow: hidden;
        font-size: 0;
    }
    .swiper-pagination-bullets-dynamic .swiper-pagination-bullet {
        transform: scale(0.33);
        position: relative;
    }
    .swiper-pagination-bullets-dynamic .swiper-pagination-bullet-active {
        transform: scale(1);
    }
    .swiper-pagination-bullets-dynamic .swiper-pagination-bullet-active-main {
        transform: scale(1);
    }
    .swiper-pagination-bullets-dynamic .swiper-pagination-bullet-active-prev {
        transform: scale(0.66);
    }
    .swiper-pagination-bullets-dynamic .swiper-pagination-bullet-active-prev-prev {
        transform: scale(0.33);
    }
    .swiper-pagination-bullets-dynamic .swiper-pagination-bullet-active-next {
        transform: scale(0.66);
    }
    .swiper-pagination-bullets-dynamic .swiper-pagination-bullet-active-next-next {
        transform: scale(0.33);
    }
    .swiper-pagination-bullet {
        width: var(--swiper-pagination-bullet-width, var(--swiper-pagination-bullet-size, 8px));
        height: var(--swiper-pagination-bullet-height, var(--swiper-pagination-bullet-size, 8px));
        display: inline-block;
        border-radius: 50%;
        background: #363331;
        opacity: 0.5;
    }
    button.swiper-pagination-bullet {
        border: none;
        margin: 0;
        padding: 0;
        box-shadow: none;
        -webkit-appearance: none;
        appearance: none;
    }
    .swiper-pagination-clickable .swiper-pagination-bullet {
        cursor: pointer;
    }
    .swiper-pagination-bullet:only-child {
        display: none !important;
    }
    .swiper-pagination-bullet-active {
        opacity: 1;
    }
    .swiper-pagination-vertical.swiper-pagination-bullets,
    .swiper-vertical > .swiper-pagination-bullets {
        right: 10px;
        top: 50%;
        transform: translate3d(0px, -50%, 0);
    }
    .swiper-pagination-vertical.swiper-pagination-bullets .swiper-pagination-bullet,
    .swiper-vertical > .swiper-pagination-bullets .swiper-pagination-bullet {
        margin: var(--swiper-pagination-bullet-vertical-gap, 6px) 0;
        display: block;
    }
    .swiper-pagination-vertical.swiper-pagination-bullets.swiper-pagination-bullets-dynamic,
    .swiper-vertical > .swiper-pagination-bullets.swiper-pagination-bullets-dynamic {
        top: 50%;
        transform: translateY(-50%);
        width: 8px;
    }
    .swiper-pagination-vertical.swiper-pagination-bullets.swiper-pagination-bullets-dynamic .swiper-pagination-bullet,
    .swiper-vertical > .swiper-pagination-bullets.swiper-pagination-bullets-dynamic .swiper-pagination-bullet {
        display: inline-block;
        transition: 0.2s transform, 0.2s top;
    }
    .swiper-horizontal > .swiper-pagination-bullets .swiper-pagination-bullet,
    .swiper-pagination-horizontal.swiper-pagination-bullets .swiper-pagination-bullet {
        margin: 0 var(--swiper-pagination-bullet-horizontal-gap, 4px);
    }
    .swiper-horizontal > .swiper-pagination-bullets.swiper-pagination-bullets-dynamic,
    .swiper-pagination-horizontal.swiper-pagination-bullets.swiper-pagination-bullets-dynamic {
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
    }
    .swiper-horizontal > .swiper-pagination-bullets.swiper-pagination-bullets-dynamic .swiper-pagination-bullet,
    .swiper-pagination-horizontal.swiper-pagination-bullets.swiper-pagination-bullets-dynamic
        .swiper-pagination-bullet {
        transition: 0.2s transform, 0.2s left;
    }
    .swiper-horizontal.swiper-rtl > .swiper-pagination-bullets-dynamic .swiper-pagination-bullet {
        transition: 0.2s transform, 0.2s right;
    }
    .swiper-pagination-progressbar {
        background: rgba(0, 0, 0, 0.25);
        position: absolute;
    }
    .swiper-pagination-progressbar .swiper-pagination-progressbar-fill {
        background: var(--swiper-pagination-color, var(--swiper-theme-color));
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        transform: scale(0);
        transform-origin: left top;
    }
    .swiper-rtl .swiper-pagination-progressbar .swiper-pagination-progressbar-fill {
        transform-origin: right top;
    }
    .swiper-horizontal > .swiper-pagination-progressbar,
    .swiper-pagination-progressbar.swiper-pagination-horizontal,
    .swiper-pagination-progressbar.swiper-pagination-vertical.swiper-pagination-progressbar-opposite,
    .swiper-vertical > .swiper-pagination-progressbar.swiper-pagination-progressbar-opposite {
        width: 100%;
        height: 4px;
        left: 0;
        top: 0;
    }
    .swiper-horizontal > .swiper-pagination-progressbar.swiper-pagination-progressbar-opposite,
    .swiper-pagination-progressbar.swiper-pagination-horizontal.swiper-pagination-progressbar-opposite,
    .swiper-pagination-progressbar.swiper-pagination-vertical,
    .swiper-vertical > .swiper-pagination-progressbar {
        width: 4px;
        height: 100%;
        left: 0;
        top: 0;
    }
    .swiper-pagination-lock {
        display: none;
    }
`;
export const GlobalStyle = createGlobalStyle`
    ${resetCSS}
    ${fontFaceCSS}
    ${fontCSS}
    ${animationCSS}
    ${swiperSlideCss}
`;
